import React, { useState, useEffect } from 'react';
import SessionGuard from './components/SessionGuard';
import { isAdminOrPlatform } from './lib/access';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from 'sonner';
import Auth from './pages/Auth';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ActivateAccount from './pages/ActivateAccount';
import Dashboard from './pages/Dashboard';
import QuerySubmit from './pages/QuerySubmit';
import ResolutionManagement from './pages/ResolutionManagement';
import TeamUsers from './pages/TeamUsers';
import Analytics from './pages/Analytics';
import FloorSpace from './pages/FloorSpace';
import OrgChartView from './pages/OrgChartView';
import System from './pages/System';
import PlatformOperations from './pages/PlatformOperations';
import GettingStarted from './pages/GettingStarted';
import Profile from './pages/Profile';
import PendingApproval from './pages/PendingApproval';
import LandingPage from './pages/LandingPage';
import AcceptInvitation from './pages/AcceptInvitation';
import ConfirmEmailChange from './pages/ConfirmEmailChange';
import PublicLegalPage from './pages/PublicLegalPage';
import Copilot from './components/Copilot';
import SidebarLayout from './components/SidebarLayout';
import { applyAppearance, DEFAULT_APPEARANCE, normalizeAppearance } from './lib/appearance';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
export const API = `${BACKEND_URL}/api`;
export const APP_LOGIN_URL = (process.env.REACT_APP_APP_LOGIN_URL || 'https://qflow-frontend-production-production.up.railway.app/auth').replace(/\/$/, '');
export const PUBLIC_SITE_URL = (process.env.REACT_APP_PUBLIC_SITE_URL || 'https://qflowresolve.com').replace(/\/$/, '');

export const axiosInstance = axios.create({
  baseURL: API,
  timeout: 30000,
});

axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function LegacyRedirect({ to }) {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axiosInstance
        .get('/auth/me')
        .then((res) => {
          setUser(res.data);
          setLoading(false);
        })
        .catch(() => {
          localStorage.removeItem('token');
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    const handleAppearanceUpdate = (event) => applyAppearance(event.detail);
    window.addEventListener('qflow:appearance-updated', handleAppearanceUpdate);

    if (user) {
      axiosInstance.get('/users/me/appearance')
        .then(({ data }) => {
          if (active) applyAppearance(normalizeAppearance(data));
        })
        .catch(() => {
          if (active) applyAppearance(DEFAULT_APPEARANCE);
        });
    } else {
      applyAppearance(DEFAULT_APPEARANCE);
    }

    return () => {
      active = false;
      window.removeEventListener('qflow:appearance-updated', handleAppearanceUpdate);
    };
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div>
      </div>
    );
  }

  const approvalPending = user?.organization_status === 'pending' || user?.onboarding_status === 'pending_platform_approval';
  const hostname = window.location.hostname.toLowerCase();
  const publicSiteHost = hostname === 'qflowresolve.com' || hostname === 'www.qflowresolve.com';

  if (user && approvalPending) {
    return (
      <div className="App">
        <Toaster position="top-center" richColors offset={80} />
        <BrowserRouter>
          <Routes>
            <Route path="*" element={<PendingApproval user={user} setUser={setUser} />} />
          </Routes>
        </BrowserRouter>
      </div>
    );
  }

  return (
    <div className="App">
      <Toaster position="top-center" richColors toastOptions={{ duration: 2500 }} offset={80} />
      <BrowserRouter>
        {user && <SessionGuard user={user} setUser={setUser} />}
        {user && <Copilot user={user} />}
        <Routes>
          <Route path="/auth" element={user ? <Navigate to="/" /> : <Auth setUser={setUser} />} />
          <Route path="/forgot-password" element={user ? <Navigate to="/" /> : <ForgotPassword />} />
          <Route path="/reset-password" element={user ? <Navigate to="/" /> : <ResetPassword />} />
          <Route path="/activate-account" element={user ? <Navigate to="/" /> : <ActivateAccount setUser={setUser} />} />
          {user ? (
            <Route element={<SidebarLayout user={user} setUser={setUser}><Routes><Route path="*" element={null} /></Routes></SidebarLayout>}>
            </Route>
          ) : null}
          <Route path="/" element={user ? <SidebarLayout user={user} setUser={setUser}><Dashboard user={user} setUser={setUser} /></SidebarLayout> : (publicSiteHost ? <LandingPage /> : <Navigate to="/auth" replace />)} />
          <Route path="/welcome" element={<LandingPage />} />
          <Route path="/privacy" element={<PublicLegalPage page="privacy" />} />
          <Route path="/terms" element={<PublicLegalPage page="terms" />} />
          <Route path="/contact" element={<PublicLegalPage page="contact" />} />
          <Route path="/accept-invitation" element={user ? <Navigate to="/" replace /> : <AcceptInvitation setUser={setUser} />} />
          <Route path="/confirm-email-change" element={<ConfirmEmailChange />} />
          <Route path="/query" element={user ? <SidebarLayout user={user} setUser={setUser}><QuerySubmit user={user} setUser={setUser} /></SidebarLayout> : <Navigate to="/auth" />} />
          <Route path="/resolutions" element={user ? <SidebarLayout user={user} setUser={setUser}><ResolutionManagement user={user} setUser={setUser} /></SidebarLayout> : <Navigate to="/auth" />} />
          <Route path="/team-users" element={user ? <SidebarLayout user={user} setUser={setUser}><TeamUsers user={user} setUser={setUser} /></SidebarLayout> : <Navigate to="/auth" />} />
          <Route path="/analytics" element={user ? <SidebarLayout user={user} setUser={setUser}><Analytics user={user} setUser={setUser} /></SidebarLayout> : <Navigate to="/auth" />} />
          <Route path="/floor-space" element={
            user
              ? (user.permissions?.can_view_floor_space === false
                  ? <Navigate to="/" />
                  : <SidebarLayout user={user} setUser={setUser}><FloorSpace user={user} setUser={setUser} /></SidebarLayout>)
              : <Navigate to="/auth" />
          } />
          <Route path="/org-chart" element={user ? <SidebarLayout user={user} setUser={setUser}><OrgChartView user={user} setUser={setUser} /></SidebarLayout> : <Navigate to="/auth" />} />
          {/* Flow Health was replaced by "Needs attention" in the Knowledge Hub. */}
          <Route path="/flow-health" element={<Navigate to="/resolutions?attention=1" replace />} />
          <Route path="/integrations" element={user ? (isAdminOrPlatform(user) ? <SidebarLayout user={user} setUser={setUser}><System user={user} setUser={setUser} integrationsOnly /></SidebarLayout> : <Navigate to="/" />) : <Navigate to="/auth" />} />
          <Route path="/system" element={<LegacyRedirect to="/admin" />} />
          <Route path="/organisation" element={<LegacyRedirect to="/admin" />} />
          <Route path="/admin" element={user ? ((isAdminOrPlatform(user) || user.permissions?.can_manage_web_mode === true) ? <SidebarLayout user={user} setUser={setUser}><System user={user} setUser={setUser} /></SidebarLayout> : <Navigate to="/" />) : <Navigate to="/auth" />} />
          <Route path="/profile" element={user ? <SidebarLayout user={user} setUser={setUser}><Profile user={user} setUser={setUser} /></SidebarLayout> : <Navigate to="/auth" />} />
          <Route path="/account" element={<LegacyRedirect to="/profile" />} />
          <Route path="/appearance" element={<LegacyRedirect to="/profile" />} />
          <Route path="/developer" element={user ? <Navigate to="/admin" replace /> : <Navigate to="/auth" />} />
          <Route path="/getting-started" element={user ? <SidebarLayout user={user} setUser={setUser}><GettingStarted user={user} /></SidebarLayout> : <Navigate to="/auth" />} />
          <Route path="/platform-operations" element={user ? (user.is_master_admin === true ? <SidebarLayout user={user} setUser={setUser}><PlatformOperations user={user} /></SidebarLayout> : <Navigate to="/" />) : <Navigate to="/auth" />} />
          <Route path="/administration" element={<Navigate to="/admin" />} />
          {/* Legacy routes redirect to new unified page */}
          <Route path="/teams" element={<Navigate to="/team-users" />} />
          <Route path="/users" element={<Navigate to="/team-users" />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
